import React, { useState, useEffect, useRef } from 'react'
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogContent, Divider, Grid, IconButton, InputAdornment,
  LinearProgress, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, alpha, createTheme, ThemeProvider, Fade
} from '@mui/material'
import {
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  Cancel as RejectIcon,
  CheckCircle as ApproveIcon,
  CheckCircleOutline as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  HourglassEmpty as PendingIcon,
  OpenInNew as OpenInNewIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material'
import {
  loadPettyExpenses,
  savePettyExpense,
  updatePettyExpense,
  deletePettyExpense
} from '../BaseURL'
import Swal from 'sweetalert2'

// Ghana Cedis symbol
var GHS = 'GH\u20b5'


var PAYMENT_COLORS = {
  Cash: 'success',
  'Mobile Money': 'info',
  'Bank Transfer': 'secondary',
  Cheque: 'warning',
  Card: 'error'
}

var EMPTY_FORM = {
  expense_date: new Date().toISOString().split('T')[0],
  amount: '',
  description: '',
  paid_to: '',
  payment_method: '',
  created_by: ''
}


// Primary brand color: #11998e
var PRIMARY = '#11998e'
var PRIMARY_DARK = '#0d7a71'
var PRIMARY_LIGHT = '#1abc9c'

var appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: PRIMARY, dark: PRIMARY_DARK, light: PRIMARY_LIGHT },
    secondary: { main: '#38ef7d', light: '#6affa0' },
    background: { default: '#f0faf8', paper: '#FFFFFF' },
    success: { main: '#11998e' },
    warning: { main: '#e6a817' },
    error: { main: '#e05252' }
  },
  typography: {
    fontFamily: '"Nunito", "Segoe UI", sans-serif',
    h5: { fontWeight: 800, letterSpacing: '-0.5px' },
    h6: { fontWeight: 700 }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 700, borderRadius: 10, boxShadow: 'none' },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0d7a71 0%, #11998e 60%, #1abc9c 100%)',
          color: '#fff',
          '&:hover': {
            background: 'linear-gradient(135deg, #0a6560 0%, #0d7a71 100%)',
            boxShadow: '0 4px 14px rgba(17,153,142,0.4)'
          }
        }
      }
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#fff',
            '&:hover fieldset': { borderColor: PRIMARY },
            '&.Mui-focused fieldset': { borderColor: PRIMARY_DARK, borderWidth: 2 }
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& th': {
            background: 'linear-gradient(135deg, #0d7a71 0%, #11998e 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            borderBottom: 'none',
            padding: '14px 16px'
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.04) },
          '&:last-child td': { borderBottom: 'none' }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: { root: { padding: '12px 16px', fontSize: '0.83rem' } }
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, fontSize: '0.7rem', height: 24 } }
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16, backgroundImage: 'none' } }
    }
  }
})

function fmtAmount(val) {
  return parseFloat(val || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })
}

function getStatusConfig(status) {
  if (status === 'approved') return { color: 'success', label: 'Approved', icon: React.createElement(ApproveIcon, { sx: { fontSize: 13 } }) }
  if (status === 'rejected') return { color: 'error',   label: 'Rejected', icon: React.createElement(RejectIcon,  { sx: { fontSize: 13 } }) }
  return { color: 'warning', label: 'Pending', icon: React.createElement(PendingIcon, { sx: { fontSize: 13 } }) }
}

// --------------------------------------------------
// FieldLabel
// --------------------------------------------------
function FieldLabel(props) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      display="block"
      mb={0.8}
      sx={{ textTransform: 'uppercase', letterSpacing: '0.7px', fontSize: '0.62rem' }}
    >
      {props.children}
    </Typography>
  )
}

// --------------------------------------------------
// StatCard
// --------------------------------------------------
function StatCard(props) {
  return (
    <Card elevation={0} sx={{ border: '1.5px solid', borderColor: alpha(props.color, 0.2), borderRadius: 3, flex: 1, minWidth: 160 }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 42, height: 42, borderRadius: 2, background: alpha(props.color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: props.color }}>
            {props.icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.62rem', fontWeight: 600 }}>
              {props.label}
            </Typography>
            <Typography sx={{ color: props.color, fontWeight: 800, lineHeight: 1.3, fontSize: '1.05rem' }}>
              {props.value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

// --------------------------------------------------
// UploadZone
// --------------------------------------------------
function UploadZone(props) {
  var inputRef = useRef()
  var dragState = useState(false)
  var drag = dragState[0]
  var setDrag = dragState[1]

  function processFile(f) {
    var reader = new FileReader()
    reader.onload = function(ev) { props.onFile(f, ev.target.result) }
    reader.readAsDataURL(f)
  }

  return (
    <Box>
      <Box
        onClick={function() { if (!props.file) inputRef.current.click() }}
        onDragOver={function(e) { e.preventDefault(); setDrag(true) }}
        onDragLeave={function() { setDrag(false) }}
        onDrop={function(e) {
          e.preventDefault()
          setDrag(false)
          var f = e.dataTransfer.files[0]
          if (f) processFile(f)
        }}
        sx={{
          border: '2px dashed',
          borderColor: drag ? PRIMARY : props.file ? 'success.main' : 'grey.300',
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
          cursor: props.file ? 'default' : 'pointer',
          transition: 'all 0.2s',
          background: drag
            ? alpha(PRIMARY, 0.05)
            : props.file
            ? alpha(PRIMARY, 0.04)
            : alpha('#F8F9FA', 0.8),
          '&:hover': !props.file ? { borderColor: PRIMARY, background: alpha(PRIMARY, 0.04) } : {}
        }}
      >
        {props.file ? (
          <Stack alignItems="center" spacing={0.8}>
            <CheckIcon sx={{ color: PRIMARY, fontSize: 32 }} />
            <Typography variant="body2" fontWeight={700} sx={{ color: PRIMARY }}>{props.file.name}</Typography>
            <Typography variant="caption" color="text.secondary">{(props.file.size / 1024).toFixed(1)} KB</Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={0.8}>
            <UploadFileIcon sx={{ fontSize: 38, color: 'grey.400' }} />
            <Typography variant="body2" fontWeight={700} color="text.secondary">Drop receipt or invoice here</Typography>
            <Typography variant="caption" color="text.disabled">or click to browse - PNG, JPG, PDF up to 10MB</Typography>
          </Stack>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={function(e) {
          var f = e.target.files[0]
          if (f) processFile(f)
        }}
      />
      {props.preview && props.preview.type && props.preview.type.startsWith('image') && (
        <Box mt={1.5} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'grey.200', maxHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f9f9' }}>
          <img src={props.preview.url} alt="receipt" style={{ maxHeight: 160, maxWidth: '100%', objectFit: 'contain' }} />
        </Box>
      )}
      {props.file && (
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={props.onClear} sx={{ mt: 1, fontSize: '0.72rem' }}>
          Remove file
        </Button>
      )}
    </Box>
  )
}

// --------------------------------------------------
// DialogHeader - reusable gradient header
// --------------------------------------------------
function DialogHeader(props) {
  return (
    <Box sx={{ background: props.gradient, color: '#fff', borderRadius: '16px 16px 0 0', px: 3, py: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {props.titleIcon}
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize="1rem" color="#fff">{props.title}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>{props.subtitle}</Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={props.onClose}
          sx={{ color: '#fff', background: 'rgba(255,255,255,0.12)', '&:hover': { background: 'rgba(255,255,255,0.22)' }, borderRadius: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  )
}

// --------------------------------------------------
// Main Component
// --------------------------------------------------
function PettyExpenses() {
  var s1 = useState([]);          var expenses = s1[0];          var setExpenses = s1[1]
  var s2 = useState(false);       var tableLoading = s2[0];      var setTableLoading = s2[1]
  var s3 = useState({ from: '', to: '', paid_to: '', created_by: '' })
  var filters = s3[0];            var setFilters = s3[1]
  var s4 = useState(false);       var showDialog = s4[0];        var setShowDialog = s4[1]
  var s5 = useState(EMPTY_FORM);  var form = s5[0];              var setForm = s5[1]
  var s6 = useState(false);       var submitting = s6[0];        var setSubmitting = s6[1]
  var s7 = useState(null);        var receiptFile = s7[0];       var setReceiptFile = s7[1]
  var s8 = useState(null);        var receiptPreview = s8[0];    var setReceiptPreview = s8[1]
  var s9  = useState(false);      var showEditDialog = s9[0];    var setShowEditDialog = s9[1]
  var s10 = useState(EMPTY_FORM); var editForm = s10[0];         var setEditForm = s10[1]
  var s11 = useState(null);       var editId = s11[0];           var setEditId = s11[1]
  var s12 = useState(null);       var editFile = s12[0];         var setEditFile = s12[1]
  var s13 = useState(null);       var editPreview = s13[0];      var setEditPreview = s13[1]
  var s14 = useState(false);      var editSubmitting = s14[0];   var setEditSubmitting = s14[1]
  var s15 = useState(null);       var deleteTarget = s15[0];     var setDeleteTarget = s15[1]
  var s16 = useState(false);      var deleting = s16[0];         var setDeleting = s16[1]
  var s17 = useState('');         var currentUser = s17[0];      var setCurrentUser = s17[1]

  useEffect(function() {
    var savedUserInfo = localStorage.getItem('userInfo')
    if (savedUserInfo) {
      var userInfo = JSON.parse(savedUserInfo)
      setCurrentUser(userInfo.name || '')
    }
  }, [])

  function fetchExpenses() {
    setTableLoading(true)
    var params = new URLSearchParams()
    Object.keys(filters).forEach(function(k) {
      if (filters[k]) params.append(k, filters[k])
    })
    fetch(loadPettyExpenses + (params.toString() ? '?' + params.toString() : ''))
      .then(function(res) { return res.json() })
      .then(function(data) { setExpenses(data.results || data) })
      .catch(function() { setExpenses([]) })
      .finally(function() { setTableLoading(false) })
  }

  useEffect(function() { fetchExpenses() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function onFilterChange(e) {
    var n = e.target.name; var v = e.target.value
    setFilters(function(p) { var x = Object.assign({}, p); x[n] = v; return x })
  }

  function onFormChange(e) {
    var n = e.target.name; var v = e.target.value
    setForm(function(p) { var x = Object.assign({}, p); x[n] = v; return x })
  }

  function onFileSet(file, dataUrl) {
    setReceiptFile(file)
    if (dataUrl) setReceiptPreview({ url: dataUrl, type: file.type })
  }

  function clearFile() { setReceiptFile(null); setReceiptPreview(null) }

  function closeAddDialog() { setShowDialog(false); setForm(EMPTY_FORM); clearFile() }

  function openEditDialog(exp) {
    setEditId(exp.id)
    setEditForm({
      expense_date:     exp.expense_date     || EMPTY_FORM.expense_date,
      amount:           exp.amount           || '',
      description:      exp.description      || '',
      paid_to:          exp.paid_to          || '',
      payment_method:   exp.payment_method   || '',
      created_by:       exp.created_by       || ''
    })
    setEditFile(null)
    setEditPreview(null)
    setShowEditDialog(true)
  }

  function closeEditDialog() { setShowEditDialog(false); setEditForm(EMPTY_FORM); setEditFile(null); setEditPreview(null) }

  function onEditFormChange(e) {
    var n = e.target.name; var v = e.target.value
    setEditForm(function(p) { var x = Object.assign({}, p); x[n] = v; return x })
  }

  function onEditFileSet(file, dataUrl) {
    setEditFile(file)
    if (dataUrl) setEditPreview({ url: dataUrl, type: file.type })
  }

  function clearEditFile() { setEditFile(null); setEditPreview(null) }

  function handleEditSubmit() {
    if (!editForm.expense_date || !editForm.amount || !editForm.paid_to || !editForm.description) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields',
        text: 'Please fill all required fields.',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'OK'
      })
      return
    }
    setEditSubmitting(true)
    var fd = new FormData()
    Object.keys(editForm).forEach(function(k) { fd.append(k, editForm[k]) })
    if (editFile) fd.append('receipt_file', editFile)
    fd.set('created_by', currentUser)
    fetch(updatePettyExpense + '/' + editId, { method: 'PUT', body: fd })
      .then(function(res) { return res.json() })
      .then(function() { closeEditDialog(); fetchExpenses() })
      .catch(function() {
        setExpenses(function(prev) {
          return prev.map(function(e) {
            if (e.id === editId) return Object.assign({}, e, editForm)
            return e
          })
        })
        closeEditDialog()
      })
      .finally(function() { setEditSubmitting(false) })
  }

  function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    fetch(deletePettyExpense + '/' + deleteTarget.id, { method: 'DELETE' })
      .then(function(res) { return res.json() })
      .then(function() { fetchExpenses() })
      .catch(function() {
        setExpenses(function(prev) { return prev.filter(function(e) { return e.id !== deleteTarget.id }) })
      })
      .finally(function() { setDeleting(false); setDeleteTarget(null) })
  }

  function handleSubmit() {
    if (!form.expense_date || !form.amount || !form.paid_to || !form.description) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields',
        text: 'Please fill all required fields.',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'OK'
      })
      return
    }
    setSubmitting(true)
    var fd = new FormData()
    Object.keys(form).forEach(function(k) { fd.append(k, form[k]) })
    if (receiptFile) fd.append('receipt_file', receiptFile)
    fd.set('created_by', currentUser)
    fetch(savePettyExpense, { method: 'POST', body: fd })
      .then(function(res) { return res.json() })
      .then(function() { closeAddDialog(); fetchExpenses() })
      .catch(function(err) {
        Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: err.message || 'Something went wrong. Please try again.',
          confirmButtonColor: '#11998e',
          confirmButtonText: 'OK'
        })
      })
      .finally(function() { setSubmitting(false) })
  }

  var total         = expenses.reduce(function(s, e) { return s + parseFloat(e.amount || 0) }, 0)
  var pendingCount  = expenses.filter(function(e) { return e.status === 'pending' }).length
  var approvedTotal = expenses.filter(function(e) { return e.status === 'approved' }).reduce(function(s, e) { return s + parseFloat(e.amount || 0) }, 0)

  return (
    <ThemeProvider theme={appTheme}>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(160deg, #e6f9f7 0%, #f0faf8 60%, #eaf7f5 100%)', p: { xs: 2, md: 4 } }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Box sx={{
            width: 50, height: 50, borderRadius: 3,
            background: 'linear-gradient(135deg, #0d7a71, #11998e)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 18px rgba(17,153,142,0.35)'
          }}>
            <ReceiptIcon sx={{ color: '#fff', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: PRIMARY_DARK }}>Petty Cash Expenses</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.4px' }}>
              Submit and track your petty cash requests
            </Typography>
          </Box>
        </Stack>

        {/* Stat Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
          <StatCard icon={<ReceiptIcon />}  label="My Requests"      value={expenses.length}                        color={PRIMARY}                        />
          <StatCard icon={<MoneyIcon />}    label="Total Submitted"  value={GHS + ' ' + fmtAmount(total)}           color={PRIMARY_DARK}                   />
          <StatCard icon={<PendingIcon />}  label="Pending Approval" value={pendingCount}                           color={appTheme.palette.warning.main}  />
          <StatCard icon={<ApproveIcon />}  label="Approved Amount"  value={GHS + ' ' + fmtAmount(approvedTotal)}   color="#0d7a71"                        />
        </Stack>

        {/* Filter Toolbar */}
        <Paper elevation={0} sx={{ p: 2, mb: 2.5 , borderColor: alpha(PRIMARY, 0.15) }}>
          <Grid container spacing={1.5} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <FieldLabel>Date From</FieldLabel>
              <TextField fullWidth type="date" name="from" value={filters.from} onChange={onFilterChange} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FieldLabel>Date To</FieldLabel>
              <TextField fullWidth type="date" name="to" value={filters.to} onChange={onFilterChange} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <FieldLabel>Paid To</FieldLabel>
              <TextField fullWidth name="paid_to" placeholder="Recipient..." value={filters.paid_to} onChange={onFilterChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><FilterIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <FieldLabel>Created By</FieldLabel>
              <TextField fullWidth name="created_by" placeholder="Staff name..." value={filters.created_by} onChange={onFilterChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><FilterIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button fullWidth variant="outlined" startIcon={<SearchIcon />} onClick={fetchExpenses}
                sx={{ height: 40, borderRadius: 2.5, borderWidth: 1.5, fontWeight: 700, borderColor: PRIMARY, color: PRIMARY, '&:hover': { borderColor: PRIMARY_DARK, color: PRIMARY_DARK, background: alpha(PRIMARY, 0.05) } }}>
                Search
              </Button>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={function() { setShowDialog(true) }}
                sx={{ height: 40, borderRadius: 2.5 }}>
                Add Expense
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper elevation={0} sx={{  overflow: 'hidden', border: '1.5px solid', borderColor: alpha(PRIMARY, 0.15) }}>
          {tableLoading && <LinearProgress sx={{ height: 3, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #11998e, #38ef7d)' } }} />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Date', 'Description' , 'Paid To', 'Amount (' + GHS + ')', 'Payment', 'Created By', 'Receipt', 'Status', 'Actions'].map(function(h) {
                    return <TableCell key={h}>{h}</TableCell>
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {!tableLoading && expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <ReceiptIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                        <Typography color="text.disabled" fontWeight={700}>No expenses found</Typography>
                        <Typography variant="caption" color="text.disabled">Adjust filters or add a new expense</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : expenses.map(function(exp, i) {
                  var cfg = getStatusConfig(exp.status)
                  return (
                    <TableRow key={exp.id || i} sx={exp.status === 'pending' ? { background: alpha('#e6a817', 0.03) } : {}}>

                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.78rem' }}>
                        {exp.expense_date}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={exp.description || ''} placement="top">
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 190 }}>
                            {exp.description}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                     

                      <TableCell sx={{ fontWeight: 600 }}>{exp.paid_to}</TableCell>

                      <TableCell>
                        <Typography fontWeight={800} sx={{ color: PRIMARY_DARK, fontFamily: '"Courier New", monospace', fontSize: '0.9rem' }}>
                          {fmtAmount(exp.amount)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip label={exp.payment_method} size="small"
                          color={PAYMENT_COLORS[exp.payment_method] || 'default'} sx={{ fontWeight: 700 }} />
                      </TableCell>

                     

                      <TableCell sx={{ fontWeight: 500 }}>{exp.created_by}</TableCell>

                      <TableCell>
                        {exp.receipt_file ? (
                          <Tooltip title="View receipt">
                            <IconButton size="small"
                              href={loadPettyExpenses.replace('loadPettyExpenses', 'receipts/') + exp.receipt_file} target="_blank"
                              sx={{ border: '1px solid', borderColor: alpha(PRIMARY, 0.3), borderRadius: 1.5, color: PRIMARY }}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">-</Typography>
                        )}
                      </TableCell>

                      {/* Status - read only */}
                      <TableCell>
                        <Tooltip title={exp.approved_by ? ('Reviewed by: ' + exp.approved_by) : 'Awaiting administrator approval'} placement="top">
                          <Chip
                            icon={cfg.icon}
                            label={cfg.label}
                            size="small"
                            color={cfg.color}
                            variant={exp.status === 'pending' ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 700, fontSize: '0.68rem', height: 26, cursor: 'default' }}
                          />
                        </Tooltip>
                        {exp.approved_by && (
                          <Typography variant="caption" display="block" color="text.disabled"
                            sx={{ fontSize: '0.62rem', mt: 0.3, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {exp.approved_by}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions - edit/delete only for pending */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {exp.status === 'pending' ? (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit this expense">
                              <IconButton size="small"
                                onClick={function() { openEditDialog(exp) }}
                                sx={{
                                  color: PRIMARY, border: '1px solid', borderColor: alpha(PRIMARY, 0.35),
                                  borderRadius: 1.5, width: 30, height: 30,
                                  '&:hover': { background: alpha(PRIMARY, 0.1) }
                                }}>
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete this expense">
                              <IconButton size="small"
                                onClick={function() { setDeleteTarget(exp) }}
                                sx={{
                                  color: 'error.main', border: '1px solid', borderColor: alpha('#e05252', 0.35),
                                  borderRadius: 1.5, width: 30, height: 30,
                                  '&:hover': { background: alpha('#e05252', 0.08) }
                                }}>
                                <DeleteIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>—</Typography>
                        )}
                      </TableCell>

                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>


        {/* ===== Add Expense Dialog ===== */}
        <Dialog open={showDialog} onClose={closeAddDialog} maxWidth="sm" fullWidth TransitionComponent={Fade}
          PaperProps={{ sx: { } }}>
          <DialogHeader
            gradient="linear-gradient(135deg, #0d7a71, #11998e 60%, #1abc9c)"
            titleIcon={<AddIcon sx={{ color: '#fff', fontSize: 22 }} />}
            title="New Petty Cash Expense"
            subtitle="Fill in the details and attach a receipt"
            onClose={closeAddDialog}
          />
          <DialogContent sx={{ p: 3 }}>
            <Grid  sx={{ mt: 0 }} spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
            <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                Expense Date *
              </span>
            </FieldLabel>

            <TextField
              fullWidth
              type="date"
              name="expense_date"
              value={form.expense_date}
              onChange={onFormChange}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }}
            />
          </Grid>

              <Grid item xs={12} sm={6} sx={{ mb: 2 }} >
               
                 <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                {'Amount (' + GHS + ') *'}
              </span>
            </FieldLabel>

                <TextField fullWidth type="number" name="amount" placeholder="0.00" value={form.amount} onChange={onFormChange}
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography fontWeight={700} color="text.secondary" fontSize="0.85rem">{GHS}</Typography></InputAdornment> }}
                   sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }} />
              </Grid>

              <Grid item xs={12} sm={6}  sx={{ mb: 2 }} >
              
              <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                Paid To *
              </span>
            </FieldLabel>
                <TextField fullWidth name="paid_to" placeholder="Recipient name or company..." value={form.paid_to} onChange={onFormChange}
                   sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }} />
              </Grid>

             
              
              <Grid item xs={12} sm={6}  sx={{ mb: 2 }}>
                
                <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                Created By / User *
              </span>
            </FieldLabel>
                <TextField fullWidth name="created_by" value={currentUser || 'No user logged in'} disabled
                 sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                },
                '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: PRIMARY_DARK, fontWeight: 700 }
              }}
               />
              </Grid>

              <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
               
               <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
               Payment Description or Narration *
              </span>
            </FieldLabel>
                <TextField fullWidth multiline rows={2} name="description" placeholder="Purpose of this expense..." value={form.description} onChange={onFormChange} 
                 sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }}
              />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                  <Chip label="Receipt / Invoice" size="small" icon={<UploadFileIcon />}
                    sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: alpha(PRIMARY, 0.3), color: PRIMARY_DARK }} />
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <UploadZone file={receiptFile} preview={receiptPreview} onFile={onFileSet} onClear={clearFile} />
              </Grid>

            </Grid>

            <Stack direction="row" justifyContent="flex-end" spacing={1.5} mt={3}>
              <Button variant="outlined" color="inherit" onClick={closeAddDialog} disabled={submitting}
                sx={{ borderRadius: 2.5, borderColor: 'grey.300', color: 'text.secondary', px: 3 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                sx={{ px: 4, borderRadius: 2.5, minWidth: 145 }}>
                {submitting ? 'Submitting...' : 'Submit Expense'}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* ===== Edit Expense Dialog ===== */}
        <Dialog open={showEditDialog} onClose={closeEditDialog} maxWidth="sm" fullWidth TransitionComponent={Fade}
          PaperProps={{ sx: { } }}>
          <DialogHeader
            gradient="linear-gradient(135deg, #0d7a71, #11998e 60%, #1abc9c)"
            titleIcon={<EditIcon sx={{ color: '#fff', fontSize: 20 }} />}
            title="Edit Petty Cash Expense"
            subtitle="Update the details below — only pending expenses can be edited"
            onClose={closeEditDialog}
          />
          <DialogContent sx={{ p: 3 }}>
            <Grid  sx={{ mt: 0 }} spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

              <Grid item xs={12} sm={6} sx={{ mb: 2 }} >
                <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                Expense Date *
              </span>
            </FieldLabel>
                <TextField fullWidth type="date" name="expense_date" value={editForm.expense_date} onChange={onEditFormChange}
                 sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }} />
              </Grid>

              <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
                <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                {'Amount (' + GHS + ') *'}
              </span>
            </FieldLabel>
                <TextField fullWidth type="number" name="amount" placeholder="0.00" value={editForm.amount} onChange={onEditFormChange}
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography fontWeight={700} color="text.secondary" fontSize="0.85rem">{GHS}</Typography></InputAdornment> }}
                  
                  sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }}
                  />
             
              </Grid>

            

              <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
                <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                Paid To *
              </span>
            </FieldLabel>
                <TextField fullWidth name="paid_to" placeholder="Recipient name or company..." value={editForm.paid_to} onChange={onEditFormChange} 
                 sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                }
              }}/>
              </Grid>

             
            
              <Grid item xs={12} sm={6}  sx={{ mb: 2 }}>
                 <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
                Created By / User *
              </span>
            </FieldLabel>
                <TextField fullWidth name="created_by" value={currentUser || 'No user logged in'} disabled
                 sx={{
                "& .MuiInputBase-root": {
                  fontSize: "1.1rem",
                  height: 48
                },
                '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: PRIMARY_DARK, fontWeight: 700 }
              }} />
              </Grid>

              <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <FieldLabel>
              <span style={{ fontSize: "0.8rem" }}>
               Payment Description or Narration *
              </span>
            </FieldLabel>
                <TextField fullWidth multiline rows={2} name="description" placeholder="Purpose of this expense..." value={editForm.description} onChange={onEditFormChange} />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                  <Chip label="Replace Receipt / Invoice" size="small" icon={<UploadFileIcon />}
                    sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: alpha(PRIMARY, 0.3), color: PRIMARY_DARK }} />
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <UploadZone file={editFile} preview={editPreview} onFile={onEditFileSet} onClear={clearEditFile} />
              </Grid>

            </Grid>

            <Stack direction="row" justifyContent="flex-end" spacing={1.5} mt={3}>
              <Button variant="outlined" color="inherit" onClick={closeEditDialog} disabled={editSubmitting}
                sx={{ borderRadius: 2.5, borderColor: 'grey.300', color: 'text.secondary', px: 3 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleEditSubmit} disabled={editSubmitting}
                startIcon={editSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                sx={{ px: 4, borderRadius: 2.5, minWidth: 155 }}>
                {editSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* ===== Delete Confirm Dialog ===== */}
        <Dialog open={Boolean(deleteTarget)} onClose={function() { if (!deleting) setDeleteTarget(null) }} maxWidth="xs" fullWidth TransitionComponent={Fade}
          PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogHeader
            gradient="linear-gradient(135deg, #7c1f1f, #e05252)"
            titleIcon={<DeleteIcon sx={{ color: '#fff', fontSize: 20 }} />}
            title="Delete Expense"
            subtitle="This action cannot be undone"
            onClose={function() { if (!deleting) setDeleteTarget(null) }}
          />
          <DialogContent sx={{ p: 3 }}>
            {deleteTarget && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}
                  sx={{ p: 2, borderRadius: 2.5, background: alpha('#e05252', 0.06), border: '1.5px solid', borderColor: alpha('#e05252', 0.18) }}>
                  <WarningIcon sx={{ color: '#e05252', fontSize: 28, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                    Are you sure you want to permanently delete this expense record?
                  </Typography>
                </Stack>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, background: '#fafafa', border: '1px solid', borderColor: 'grey.200' }}>
                  <Stack spacing={0.8}>
                    {[
                      { label: 'Paid To',    value: deleteTarget.paid_to },
                      { label: 'Amount',     value: GHS + ' ' + fmtAmount(deleteTarget.amount) },
                      { label: 'Date',       value: deleteTarget.expense_date }
                    ].map(function(row) {
                      return (
                        <Stack key={row.label} direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={600}
                            sx={{ textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.62rem' }}>
                            {row.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>{row.value}</Typography>
                        </Stack>
                      )
                    })}
                  </Stack>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <Stack direction="row" spacing={1.5} sx={{ px: 3, pb: 3, pt: 0 }}>
            <Button fullWidth variant="outlined" color="inherit" onClick={function() { setDeleteTarget(null) }} disabled={deleting}
              sx={{ borderRadius: 2.5, borderColor: 'grey.300', color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button fullWidth variant="contained" onClick={handleDelete} disabled={deleting}
              startIcon={deleting ? <CircularProgress size={15} color="inherit" /> : <DeleteIcon />}
              sx={{
                borderRadius: 2.5, fontWeight: 700,
                background: 'linear-gradient(135deg, #7c1f1f, #e05252)',
                '&:hover': { background: 'linear-gradient(135deg, #6b1a1a, #c0392b)', boxShadow: '0 4px 14px rgba(224,82,82,0.4)' },
                '&.Mui-disabled': { opacity: 0.5 }
              }}>
              {deleting ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </Stack>
        </Dialog>

      </Box>
    </ThemeProvider>
  )
}

export default PettyExpenses